import { __rest, __assign } from '../../external/tslib/tslib.es6.mjs.js';
import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { useRef, useEffect } from 'react';
import { MaskedRange } from 'imask';
import { useIMask } from 'react-imask';
import { parseDate as $fae977aafc393c5c$export$6b862160d295c8e } from '../../external/@internationalized/date/dist/import.mjs.js';
import dayjs from 'dayjs';
import { useOverlayTrigger } from 'react-aria';
import { useOverlayTriggerState } from 'react-stately';
import { Input } from '../input/Input.js';
import { Popover } from '../../base/popover/Popover.js';
import { Calendar } from '../../base/calendar/Calendar.js';
import { SmallCalendarIcon } from '../../icons/SmallCalendarIcon.js';
import { constants } from '../../constants/index.js';
import { useSetNativeValue } from '../../hooks/useSetNativeValue/useSetNativeValue.js';
import styles from './DateInput.module.scss.js';

var MIN_YEAR = 1900;
var MAX_YEAR = dayjs().year() + 100;
var ISO_FORMAT = 'YYYY-MM-DD';
var maskOptions = {
    overwrite: true,
    autofix: true,
    lazy: true,
    mask: constants.DATE_INPUT_FORMAT,
    blocks: {
        MM: {
            mask: MaskedRange,
            placeholderChar: 'MM',
            from: 1,
            to: 12,
            maxLength: 2,
        },
        DD: {
            mask: MaskedRange,
            placeholderChar: 'DD',
            from: 1,
            to: 31,
            maxLength: 2,
        },
        YYYY: {
            mask: MaskedRange,
            placeholderChar: 'YYYY',
            from: MIN_YEAR,
            to: MAX_YEAR,
            maxLength: 4,
        },
    },
};
var autoCorrectValue = function (value, minValue, maxValue, isDateUnavailable) {
    var date = dayjs(value);
    if (date.isValid()) {
        if (date.year() < MIN_YEAR || date.year() > MAX_YEAR) {
            return '';
        }
        var calendarDate = $fae977aafc393c5c$export$6b862160d295c8e(date.format(ISO_FORMAT));
        if ((minValue && minValue.compare(calendarDate) > 0) ||
            (maxValue && maxValue.compare(calendarDate) < 0) ||
            (isDateUnavailable && isDateUnavailable(calendarDate))) {
            return '';
        }
        return date.format(constants.DATE_INPUT_FORMAT);
    }
    return '';
};
var DateInput = function (_a) {
    var inputProps = _a.inputProps, size = _a.size, minValue = _a.minValue, maxValue = _a.maxValue, isDateUnavailable = _a.isDateUnavailable, props = __rest(_a, ["inputProps", "size", "minValue", "maxValue", "isDateUnavailable"]);
    var _b = useIMask(maskOptions), ref = _b.ref, maskRef = _b.maskRef;
    var state = useOverlayTriggerState({});
    var triggerRef = useRef(null);
    var _c = useOverlayTrigger({ type: 'dialog' }, state, triggerRef), _d = _c.triggerProps; _d.onPress; var triggerProps = __rest(_d, ["onPress"]), overlayProps = _c.overlayProps;
    var setNativeValue = useSetNativeValue();
    // Update a value of the iMask when init value provided in the input
    useEffect(function () {
        if (inputProps.value) {
            ref.current.value = inputProps.value;
            maskRef.current.updateValue();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // Updates only when value is correct
    var onChange = function (e) {
        var correctedValue = autoCorrectValue(e.target.value, minValue, maxValue, isDateUnavailable);
        if (correctedValue === e.target.value) {
            maskRef.current.updateValue();
            inputProps.onChange(e);
        }
    };
    // Check and correct a value when possible (if value not valid then clear the input)
    var onBlur = function (event) {
        var _a;
        var input = ref.current;
        setNativeValue(input, autoCorrectValue(maskRef.current.value, minValue, maxValue, isDateUnavailable));
        maskRef.current.updateValue();
        inputProps.onChange(event);
        (_a = inputProps.onBlur) === null || _a === void 0 ? void 0 : _a.call(inputProps, event);
    };
    var onPickDate = function (date) {
        var input = ref.current;
        var value = dayjs(date.toString()).format(constants.DATE_INPUT_FORMAT);
        setNativeValue(input, value);
        maskRef.current.updateValue();
        input.dispatchEvent(new Event('change', { bubbles: true }));
        state.close();
    };
    var date = dayjs(inputProps.value);
    var calendarDate = inputProps.value && date ? $fae977aafc393c5c$export$6b862160d295c8e(date.format(ISO_FORMAT)) : undefined;
    return (jsxs(Fragment, { children: [jsx(Input, __assign({ placeholder: constants.DATE_INPUT_FORMAT, maxLength: 10, ref: ref }, props, { size: size, inputProps: __assign(__assign({}, inputProps), { value: undefined, onBlur: onBlur, onChange: onChange }), rightElement: jsx(Input.TriggerButton, __assign({ size: size, ref: triggerRef, onClick: state.toggle }, triggerProps, { "aria-label": "Date picker" }, { children: jsx(SmallCalendarIcon, {}) })) })), jsx(Popover, __assign({ triggerRef: triggerRef, isOpen: state.isOpen, onClose: state.close, placement: "bottom end", className: styles.popover, hasFocusTrap: true, hasAutoFocus: true }, overlayProps, { children: jsx(Calendar, { defaultValue: calendarDate, minValue: minValue, maxValue: maxValue, isDateUnavailable: isDateUnavailable, onChange: onPickDate }) }))] }));
};

export { DateInput };
